import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  User, 
  Briefcase, 
  Dna, 
  Download, 
  Share2, 
  Loader2,
  Copy,
  Check,
  Twitter,
  Linkedin,
  Star,
  Target,
  TrendingUp,
  Award,
  FileImage,
  FileText
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/aira-echo-logo.png';

export default function ViralCardsPage() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('future');
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const cardRef = useRef(null);

  // Profile form for card generation
  const [profile, setProfile] = useState({
    skills: [],
    interests: [],
    career_goals: '',
    industry: '',
    experience_years: 0,
    education: ''
  });
  const [skillInput, setSkillInput] = useState('');
  const [interestInput, setInterestInput] = useState('');

  // Business idea form
  const [businessForm, setBusinessForm] = useState({
    industry: '',
    budget: '',
    interests: [],
    experience: ''
  });

  const loadProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/viral/profile`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to load profile');
    }
  }, [token]);

  const loadReports = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/viral/reports`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to load reports');
    }
  }, [token]);

  useEffect(() => {
    loadProfile();
    loadReports();
  }, [loadProfile, loadReports]);

  const saveProfile = async () => {
    try {
      await axios.patch(`${API_URL}/api/viral/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Profile saved');
    } catch (error) {
      toast.error('Failed to save profile');
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !profile.skills.includes(skillInput.trim())) {
      setProfile(prev => ({ ...prev, skills: [...prev.skills, skillInput.trim()] }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setProfile(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addInterest = () => {
    if (interestInput.trim() && !profile.interests.includes(interestInput.trim())) {
      setProfile(prev => ({ ...prev, interests: [...prev.interests, interestInput.trim()] }));
      setInterestInput('');
    }
  };

  const removeInterest = (interest) => {
    setProfile(prev => ({ ...prev, interests: prev.interests.filter(i => i !== interest) }));
  };

  const generateFutureIdentity = async () => {
    setLoading(true);
    try {
      await saveProfile();
      const response = await axios.post(`${API_URL}/api/viral/future-identity`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCurrentReport(response.data);
      loadReports();
      toast.success('Future Identity Card generated!');
    } catch (error) {
      toast.error('Failed to generate card');
    } finally {
      setLoading(false);
    }
  };

  const generateCareerDNA = async () => {
    setLoading(true);
    try {
      await saveProfile();
      const response = await axios.post(`${API_URL}/api/viral/career-dna`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCurrentReport(response.data);
      loadReports();
      toast.success('Career DNA Card generated!');
    } catch (error) {
      toast.error('Failed to generate card');
    } finally {
      setLoading(false);
    }
  };

  const generateBusinessIdea = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/viral/business-idea`, businessForm, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCurrentReport(response.data);
      loadReports();
      toast.success('Business Idea Report generated!');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const copyShareLink = () => {
    if (currentReport?.share_url) {
      navigator.clipboard.writeText(`${window.location.origin}${currentReport.share_url}`);
      setCopiedLink(true);
      toast.success('Link copied!');
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const shareOnTwitter = () => {
    const text = `Check out my ${currentReport?.report_type?.replace('_', ' ')} from AIRA ECHO! 🚀`;
    const url = `${window.location.origin}${currentReport?.share_url}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = `${window.location.origin}${currentReport?.share_url}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const [downloadingPNG, setDownloadingPNG] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const downloadAsPNG = async () => {
    if (!cardRef.current) return;
    setDownloadingPNG(true);
    
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#030014',
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `${currentReport?.report_type}_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Card downloaded as PNG!');
    } catch (error) {
      toast.error('Failed to download PNG');
      console.error(error);
    } finally {
      setDownloadingPNG(false);
    }
  };

  const downloadAsPDF = async () => {
    if (!cardRef.current) return;
    setDownloadingPDF(true);
    
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#030014',
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`${currentReport?.report_type}_${Date.now()}.pdf`);
      toast.success('Card downloaded as PDF!');
    } catch (error) {
      toast.error('Failed to download PDF');
      console.error(error);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const tabs = [
    { id: 'future', label: 'Future Identity', icon: Star },
    { id: 'career', label: 'Career DNA', icon: Dna },
    { id: 'business', label: 'Business Idea', icon: Briefcase },
  ];

  return (
    <div className="max-w-6xl mx-auto" data-testid="viral-cards-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">
          <span className="glow-text">Viral Cards</span>
        </h1>
        <p className="text-gray-400">Generate shareable AI-powered insights about your future</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8 justify-center">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-primary to-secondary text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid={`${tab.id}-tab`}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-6">
          {/* Profile Form for Future Identity & Career DNA */}
          {(activeTab === 'future' || activeTab === 'career') && (
            <GlassCard className="p-6">
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Your Profile
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Skills</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                      className="input-field flex-1"
                      placeholder="Add a skill..."
                      data-testid="skill-input"
                    />
                    <button onClick={addSkill} className="btn-secondary px-4">Add</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill) => (
                      <span key={skill} className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm flex items-center gap-2">
                        {skill}
                        <button onClick={() => removeSkill(skill)} className="hover:text-red-400">&times;</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Interests</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={interestInput}
                      onChange={(e) => setInterestInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                      className="input-field flex-1"
                      placeholder="Add an interest..."
                      data-testid="interest-input"
                    />
                    <button onClick={addInterest} className="btn-secondary px-4">Add</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profile.interests.map((interest) => (
                      <span key={interest} className="px-3 py-1 bg-accent/20 text-accent rounded-full text-sm flex items-center gap-2">
                        {interest}
                        <button onClick={() => removeInterest(interest)} className="hover:text-red-400">&times;</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Industry</label>
                  <input
                    type="text"
                    value={profile.industry}
                    onChange={(e) => setProfile(prev => ({ ...prev, industry: e.target.value }))}
                    className="input-field w-full"
                    placeholder="e.g., Technology, Finance, Healthcare"
                    data-testid="industry-input"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Career Goals</label>
                  <textarea
                    value={profile.career_goals}
                    onChange={(e) => setProfile(prev => ({ ...prev, career_goals: e.target.value }))}
                    className="input-field w-full h-20 resize-none"
                    placeholder="What do you want to achieve?"
                    data-testid="goals-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Experience (years)</label>
                    <input
                      type="number"
                      value={profile.experience_years}
                      onChange={(e) => setProfile(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                      className="input-field w-full"
                      min="0"
                      data-testid="experience-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Education</label>
                    <input
                      type="text"
                      value={profile.education}
                      onChange={(e) => setProfile(prev => ({ ...prev, education: e.target.value }))}
                      className="input-field w-full"
                      placeholder="e.g., B.Tech, MBA"
                      data-testid="education-input"
                    />
                  </div>
                </div>

                <button
                  onClick={activeTab === 'future' ? generateFutureIdentity : generateCareerDNA}
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="generate-card-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate {activeTab === 'future' ? 'Future Identity' : 'Career DNA'} Card
                    </>
                  )}
                </button>
              </div>
            </GlassCard>
          )}

          {/* Business Idea Form */}
          {activeTab === 'business' && (
            <GlassCard className="p-6">
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                Business Preferences
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Industry Interest</label>
                  <input
                    type="text"
                    value={businessForm.industry}
                    onChange={(e) => setBusinessForm(prev => ({ ...prev, industry: e.target.value }))}
                    className="input-field w-full"
                    placeholder="e.g., EdTech, FinTech, Healthcare"
                    data-testid="biz-industry-input"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Budget Range</label>
                  <select
                    value={businessForm.budget}
                    onChange={(e) => setBusinessForm(prev => ({ ...prev, budget: e.target.value }))}
                    className="input-field w-full"
                    data-testid="budget-select"
                  >
                    <option value="">Select budget</option>
                    <option value="Under ₹1 Lakh">Under ₹1 Lakh</option>
                    <option value="₹1-5 Lakhs">₹1-5 Lakhs</option>
                    <option value="₹5-15 Lakhs">₹5-15 Lakhs</option>
                    <option value="₹15-50 Lakhs">₹15-50 Lakhs</option>
                    <option value="Above ₹50 Lakhs">Above ₹50 Lakhs</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Your Experience</label>
                  <select
                    value={businessForm.experience}
                    onChange={(e) => setBusinessForm(prev => ({ ...prev, experience: e.target.value }))}
                    className="input-field w-full"
                    data-testid="biz-experience-select"
                  >
                    <option value="">Select experience level</option>
                    <option value="First-time entrepreneur">First-time entrepreneur</option>
                    <option value="Some business experience">Some business experience</option>
                    <option value="Serial entrepreneur">Serial entrepreneur</option>
                    <option value="Corporate professional">Corporate professional</option>
                  </select>
                </div>

                <button
                  onClick={generateBusinessIdea}
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="generate-biz-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Business Idea
                    </>
                  )}
                </button>
              </div>
            </GlassCard>
          )}

          {/* Previous Reports */}
          <GlassCard className="p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Your Reports</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
              {reports.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No reports yet</p>
              ) : (
                reports.map((report) => (
                  <div
                    key={report.report_id}
                    onClick={() => setCurrentReport(report)}
                    className={`p-3 rounded-xl cursor-pointer transition-colors ${
                      currentReport?.report_id === report.report_id
                        ? 'bg-primary/20 border border-primary/50'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <p className="font-medium truncate">{report.title}</p>
                    <p className="text-xs text-gray-500 capitalize">{report.report_type?.replace('_', ' ')}</p>
                  </div>
                ))
              )}
            </div>
          </GlassCard>
        </div>

        {/* Card Preview */}
        <div>
          <AnimatePresence mode="wait">
            {currentReport ? (
              <motion.div
                key={currentReport.report_id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                ref={cardRef}
              >
                {/* Future Identity Card */}
                {currentReport.report_type === 'future_identity' && (
                  <GlassCard className="p-8 relative overflow-hidden" data-testid="future-identity-card">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-primary/30 to-transparent rounded-bl-full" />
                    <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-secondary/30 to-transparent rounded-tr-full" />
                    <div className="absolute top-4 right-4 opacity-20">
                      <img src={LOGO_URL} alt="AIRA" className="w-12 h-12" crossOrigin="anonymous" />
                    </div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-6">
                        <Star className="w-6 h-6 text-accent" />
                        <span className="text-sm text-accent font-medium tracking-wider">FUTURE IDENTITY CARD</span>
                      </div>
                      
                      <h2 className="font-heading text-3xl font-bold mb-2">{currentReport.content?.user_name}</h2>
                      <p className="text-xl text-primary mb-6 font-medium">{currentReport.content?.future_title}</p>
                      
                      <p className="text-gray-300 mb-6 leading-relaxed">{currentReport.content?.career_path}</p>
                      
                      <div className="grid grid-cols-2 gap-6 mb-6">
                        <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                          <h4 className="text-sm text-gray-500 mb-3 font-medium">Key Strengths</h4>
                          <div className="space-y-2">
                            {currentReport.content?.key_strengths?.map((s, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-primary to-accent" />
                                <span className="text-sm">{s}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                          <h4 className="text-sm text-gray-500 mb-3 font-medium">Predicted Achievements</h4>
                          <div className="space-y-2">
                            {currentReport.content?.predicted_achievements?.map((a, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <Award className="w-4 h-4 text-accent flex-shrink-0" />
                                <span className="text-sm">{a}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-gradient-to-r from-primary/15 to-secondary/15 rounded-xl border border-primary/20">
                        <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Life Direction</p>
                        <p className="font-medium text-white">{currentReport.content?.life_direction}</p>
                      </div>
                      
                      <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <img src={LOGO_URL} alt="AIRA ECHO" className="w-6 h-6" crossOrigin="anonymous" />
                          <span className="text-xs text-gray-500">Generated by AIRA ECHO</span>
                        </div>
                        <span className="text-xs text-gray-600">askaira.ai</span>
                      </div>
                    </div>
                  </GlassCard>
                )}

                {/* Career DNA Card */}
                {currentReport.report_type === 'career_dna' && (
                  <GlassCard className="p-8 relative overflow-hidden" data-testid="career-dna-card">
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary via-accent to-secondary" />
                    <div className="absolute top-4 right-4 opacity-20">
                      <img src={LOGO_URL} alt="AIRA" className="w-12 h-12" crossOrigin="anonymous" />
                    </div>
                    
                    <div className="flex items-center gap-2 mb-6 mt-2">
                      <Dna className="w-6 h-6 text-accent" />
                      <span className="text-sm text-accent font-medium tracking-wider">CAREER DNA ANALYSIS</span>
                    </div>
                    
                    <h2 className="font-heading text-3xl font-bold mb-2">{currentReport.content?.user_name}</h2>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl text-primary font-semibold">{currentReport.content?.career_archetype}</span>
                      <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-xs">Archetype</span>
                    </div>
                    <p className="text-gray-400 mb-6">{currentReport.content?.personality_type}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                        <h4 className="text-sm text-gray-500 mb-3 font-medium">Dominant Skills</h4>
                        <div className="flex flex-wrap gap-2">
                          {currentReport.content?.dominant_skills?.map((s, i) => (
                            <span key={i} className="px-3 py-1 bg-gradient-to-r from-primary/20 to-primary/10 text-primary rounded-full text-sm border border-primary/30">{s}</span>
                          ))}
                        </div>
                      </div>
                      <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                        <h4 className="text-sm text-gray-500 mb-3 font-medium">Ideal Industries</h4>
                        <div className="flex flex-wrap gap-2">
                          {currentReport.content?.ideal_industries?.map((s, i) => (
                            <span key={i} className="px-3 py-1 bg-gradient-to-r from-accent/20 to-accent/10 text-accent rounded-full text-sm border border-accent/30">{s}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-6">
                      <h4 className="text-sm text-gray-500 font-medium">Career Recommendations</h4>
                      {currentReport.content?.career_recommendations?.map((r, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/5 hover:border-primary/30 transition-colors">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20 flex items-center justify-center">
                            <Target className="w-4 h-4 text-primary" />
                          </div>
                          <span>{r}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="p-4 bg-gradient-to-r from-green-500/15 to-emerald-500/15 rounded-xl border border-green-500/20">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Leadership Potential</p>
                      <p className="font-medium text-green-400">{currentReport.content?.leadership_potential}</p>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src={LOGO_URL} alt="AIRA ECHO" className="w-6 h-6" crossOrigin="anonymous" />
                        <span className="text-xs text-gray-500">Generated by AIRA ECHO</span>
                      </div>
                      <span className="text-xs text-gray-600">askaira.ai</span>
                    </div>
                  </GlassCard>
                )}

                {/* Business Idea Report */}
                {currentReport.report_type === 'business_idea' && (
                  <GlassCard className="p-8 relative overflow-hidden" data-testid="business-idea-card">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/20 to-transparent rounded-bl-full" />
                    <div className="absolute top-4 right-4 opacity-20">
                      <img src={LOGO_URL} alt="AIRA" className="w-12 h-12" crossOrigin="anonymous" />
                    </div>
                    
                    <div className="flex items-center gap-2 mb-6">
                      <Briefcase className="w-6 h-6 text-primary" />
                      <span className="text-sm text-primary font-medium tracking-wider">BUSINESS IDEA REPORT</span>
                    </div>
                    
                    <h2 className="font-heading text-3xl font-bold mb-3">{currentReport.content?.idea_title}</h2>
                    <p className="text-gray-300 mb-6 leading-relaxed">{currentReport.content?.idea_summary}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="p-4 bg-gradient-to-r from-accent/10 to-accent/5 rounded-xl border border-accent/20">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Investment Required</p>
                        <p className="font-bold text-xl text-accent">{currentReport.content?.estimated_investment}</p>
                      </div>
                      <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Target Market</p>
                        <p className="font-semibold">{currentReport.content?.target_market}</p>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-sm text-gray-500 mb-3 font-medium">Growth Opportunities</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {currentReport.content?.growth_opportunities?.map((g, i) => (
                          <div key={i} className="flex items-center gap-2 p-3 bg-green-500/10 rounded-xl border border-green-500/20">
                            <TrendingUp className="w-4 h-4 text-green-400 flex-shrink-0" />
                            <span className="text-sm">{g}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-sm text-gray-500 mb-3 font-medium">Recommended First Steps</h4>
                      <div className="space-y-2">
                        {currentReport.content?.recommended_first_steps?.map((s, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/5">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-sm font-bold">{i + 1}</div>
                            <span className="text-sm">{s}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {currentReport.content?.revenue_model && (
                      <div className="p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-primary/20">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Revenue Model</p>
                        <p className="font-medium">{currentReport.content?.revenue_model}</p>
                      </div>
                    )}
                    
                    <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src={LOGO_URL} alt="AIRA ECHO" className="w-6 h-6" crossOrigin="anonymous" />
                        <span className="text-xs text-gray-500">Generated by AIRA ECHO</span>
                      </div>
                      <span className="text-xs text-gray-600">askaira.ai</span>
                    </div>
                  </GlassCard>
                )}

                {/* Share Options */}
                <div className="mt-6 space-y-3">
                  <div className="flex gap-3">
                    <button onClick={copyShareLink} className="btn-secondary flex-1 flex items-center justify-center gap-2" data-testid="copy-link-btn">
                      {copiedLink ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                      {copiedLink ? 'Copied!' : 'Copy Link'}
                    </button>
                    <button onClick={shareOnTwitter} className="btn-secondary px-4" title="Share on Twitter" data-testid="share-twitter-btn">
                      <Twitter className="w-5 h-5" />
                    </button>
                    <button onClick={shareOnLinkedIn} className="btn-secondary px-4" title="Share on LinkedIn" data-testid="share-linkedin-btn">
                      <Linkedin className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="flex gap-3">
                    <button 
                      onClick={downloadAsPNG} 
                      disabled={downloadingPNG}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                      data-testid="download-png-btn"
                    >
                      {downloadingPNG ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <FileImage className="w-5 h-5" />
                      )}
                      Download PNG
                    </button>
                    <button 
                      onClick={downloadAsPDF}
                      disabled={downloadingPDF}
                      className="btn-secondary flex-1 flex items-center justify-center gap-2"
                      data-testid="download-pdf-btn"
                    >
                      {downloadingPDF ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <FileText className="w-5 h-5" />
                      )}
                      Download PDF
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <GlassCard className="p-12 text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Generate Your Card</h3>
                  <p className="text-gray-400">Fill in your profile and generate a shareable AI-powered card</p>
                </GlassCard>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
